import { useEffect } from 'react';

import styled from '@emotion/styled';

import { helper } from '@myrealtrip/frontend-weblog';
import { BottomSheet, Text } from '@myrealtrip/web-ui';

import { WeblogEventName, WeblogPageCategory, WeblogScreenName } from '~constants/weblog';
import useDateSearch from '~hooks/pointToPointTicket/useDateSearch';

import CalendarBody from './SearchType/Date/CalendarBody';

const { sendLayerViewLog } = helper;

interface Props {
  isOpen: boolean;
  isSingleTrip: boolean;
  departureDate: string | null;
  arrivalDate: string | null | undefined;
  showTimeOption?: boolean;
  outboundDepartureTime?: string | null;
  inboundDepartureTime?: string | null;
  onApply: (params: {
    startDate: string;
    outboundStartTime?: string;
    endDate?: string;
    inboundStartTime?: string;
  }) => void;
  onClose: () => void;
}

const DatePickerSheet = ({
  isOpen,
  isSingleTrip,
  departureDate,
  arrivalDate,
  showTimeOption,
  outboundDepartureTime,
  inboundDepartureTime,
  onApply,
  onClose,
}: Props) => {
  const {
    startDate,
    endDate,
    outboundStartTime,
    inboundStartTime,
    handleChangeSingleTripDate,
    handleChangeRoundTripDates,
    handleChangeDepartureTime,
    handleApply,
    handleReset,
  } = useDateSearch({
    isSingleTrip,
    departureDate,
    arrivalDate,
    outboundDepartureTime,
    inboundDepartureTime,
    onApply,
  });

  useEffect(() => {
    if (!isOpen) return;

    sendLayerViewLog({
      pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
      screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
      eventName: WeblogEventName.SELECT_DATE,
      itemName: '날짜 선택',
    });
  }, [isOpen]);

  return (
    <BottomSheet
      isOpen={isOpen}
      detent="full"
      headerProps={{
        titleAlign: 'center',
        children: (
          <CalendarTitleText typography="headlineNormal17" as="p" align="center">
            날짜 선택
          </CalendarTitleText>
        ),
        closable: true,
        hasGrapple: true,
      }}
      backdropProps={{
        onClick: onClose,
      }}
      preventScrollProps={{ ignoreIosScroll: true }}
      contentProps={{
        disableDrag: true,
      }}
      onXClick={onClose}
    >
      <Inner>
        <CalendarBody
          isSingleTrip={isSingleTrip}
          startDate={startDate}
          endDate={endDate}
          onApply={handleApply}
          onChangeDate={handleChangeSingleTripDate}
          onRangeChangeDate={handleChangeRoundTripDates}
          onReset={handleReset}
          onChangeTime={handleChangeDepartureTime}
          showTimeOption={showTimeOption}
          outboundStartTime={outboundStartTime}
          inboundStartTime={inboundStartTime}
        />
        <OverflowOverlay />
      </Inner>
    </BottomSheet>
  );
};

const Inner = styled.div`
  height: 100%;
  padding: 0 0 117px;

  & .range,
  .single {
    height: 100%;

    & > div {
      height: 100%;
    }
  }
`;

const OverflowOverlay = styled.div`
  position: fixed;
  right: 0;
  bottom: -25vh;
  left: 0;
  width: 100vw;
  height: 50vh;
  background-color: ${({ theme: { colors } }) => colors.white};
  pointer-events: none;
  z-index: -1;
`;

const CalendarTitleText = styled(Text)`
  color: ${({ theme: { colors } }) => colors.gray.s1000};
`;

export default DatePickerSheet;
